const PLATFORMS = ['P3B', 'R3C']

// Check if a file name contains any platform identifier
export function hasPlatformIdentifier(fileName: string): boolean {
  return PLATFORMS.some(p => fileName.includes(`_${p}`))
}

// Strip the platform identifier from a file name for display
// e.g. "prog_v1002062_evse_minOS12_P3B_rc" → "prog_v1002062_evse_minOS12_rc"
// Also strips "_platform" so saved Firebase values display cleanly
export function stripPlatform(fileName: string): string {
  let result = fileName
  for (const p of PLATFORMS) {
    result = result.replace(`_${p}`, '')
  }
  result = result.replace('_platform', '')
  return result
}

// Replace the platform identifier with "platform" for saving
// e.g. "prog_v1002062_evse_minOS12_P3B_rc" → "prog_v1002062_evse_minOS12_platform_rc"
export function toPlatformVersion(originalFileName: string): string {
  let result = originalFileName
  for (const p of PLATFORMS) {
    if (result.includes(`_${p}`)) {
      result = result.replace(`_${p}`, '_platform')
      break
    }
  }
  return result
}

// Deduplicate file list by stripping platforms, keeping unique display names
export function deduplicateFiles(files: string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const file of files) {
    const display = stripPlatform(file)
    if (!seen.has(display)) {
      seen.add(display)
      result.push(display)
    }
  }
  return result
}

// Find which platform variants exist for a given display name
export function getAvailablePlatforms(displayName: string, allFiles: string[]): string[] {
  return PLATFORMS.filter(p => {
    return allFiles.some(f => stripPlatform(f) === displayName && f.includes(`_${p}`))
  })
}

// Find which platform variants are missing for a given display name
export function getMissingPlatforms(displayName: string, allFiles: string[]): string[] {
  const available = getAvailablePlatforms(displayName, allFiles)
  return PLATFORMS.filter(p => !available.includes(p))
}

// Check if any original file with this display name had a platform identifier
export function displayNameHadPlatform(displayName: string, allFiles: string[]): boolean {
  return allFiles.some(f => stripPlatform(f) === displayName && hasPlatformIdentifier(f))
}

// Resolve a display name to what should be saved to Firebase
// Returns the "platform" version if the file had platform identifiers, otherwise returns as-is
export function resolveForSave(displayName: string, allFiles: string[]): string {
  if (!displayNameHadPlatform(displayName, allFiles)) return displayName

  // Find any original file that matches, and replace its platform with "platform"
  const original = allFiles.find(f => stripPlatform(f) === displayName && hasPlatformIdentifier(f))
  if (original) return toPlatformVersion(original)

  return displayName
}

export { PLATFORMS }
